import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../axiosInstace";
import CheckList from "../CheckList/CheckList";
import "../EmployeeTable/EmployeeTable.scss";
import "./UpdateProjectMembers.scss";
import Toast from "../Toast/Toast";

export default function UpdateProjectMembers({
  projectId = null,
  creatorEmpId = null,
  onCancel = () => {},
  onConfirm = () => {},
  currentEmpId = null,
}) {
  const [employees, setEmployees] = useState([]);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [addedMap, setAddedMap] = useState({});
  const [selectedRoles, setSelectedRoles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success");

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [empRes, memRes, roleRes] = await Promise.all([
          axiosInstance.get("/api/emp/getall"),
          axiosInstance.get(
            `/api/emp/projects/projectmemberaccess/${projectId}/allmembers`
          ),
          axiosInstance.get("/api/emp/role/getall").catch(() => ({ data: [] })),
        ]);

        setEmployees(empRes?.data?.data ?? []);
        setMembers(memRes?.data?.data ?? []);
        setRoles(roleRes?.data?.data ?? []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    if (projectId) load();
  }, [projectId]);

  /* ================= ROLES ================= */
  const ROLE_OPTIONS = useMemo(() => {
    if (!roles.length) {
      return [
        { label: "Viewer", value: 4 },
        { label: "Approver", value: 2 },
        { label: "Doer", value: 3 },
        { label: "Creator", value: 1 },
        { label: "Client", value: 5 },
      ];
    }
    return roles.map((r) => ({
      label: r.roleType ?? r.roleName ?? r.name,
      value: Number(r.roleId ?? r.id),
    }));
  }, [roles]);

  /* ================= NOT MEMBERS FIRST ================= */
  const notMembers = useMemo(() => {
    const q = search.trim().toLowerCase();

    const base = employees.filter(
      (e) => !members.some((m) => Number(m.empId) === Number(e.empId))
    );

    if (!q) return base;

    return base.filter(
      (e) =>
        e.empName?.toLowerCase().includes(q) ||
        e.departmentName?.toLowerCase().includes(q) ||
        e.roleName?.toLowerCase().includes(q)
    );
  }, [employees, members, search]);

  /* ================= HELPERS ================= */
  const getInitial = (name = "") =>
    name?.trim()?.charAt(0)?.toUpperCase() || "U";

  const getColorForName = (name = "") => {
    const colors = [
      "#2563EB",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
      "#F97316",
    ];
    const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  /* ================= HANDLERS ================= */
  const handleRolePillClick = (empId, roleId, disabled) => {
    if (disabled) return;
    setSelectedRoles((p) => ({ ...p, [empId]: roleId }));
    setAddedMap((p) => ({ ...p, [empId]: true }));
  };

  const handleCheckboxChange = (emp, e) => {
    const checked = e?.target ? e.target.checked : !!e;
    setAddedMap((p) => ({ ...p, [emp.empId]: checked }));

    if (checked && !selectedRoles[emp.empId]) {
      setSelectedRoles((p) => ({
        ...p,
        [emp.empId]: ROLE_OPTIONS[0]?.value ?? null,
      }));
    }
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const selected = Object.entries(addedMap)
      .filter(([_, v]) => v)
      .map(([empId]) => ({
        empId: Number(empId),
        roleId: selectedRoles[empId],
      }))
      .filter((r) => r.roleId != null);

    if (!selected.length) {
      setToastMsg("Please select at least one employee.");
      setToastType("info");
      setToastOpen(true);
      return;
    }

    try {
      setSubmitting(true);
      await axiosInstance.post(
        `/api/emp/projects/projectmemberaccess/${projectId}/members/add`,
        { projectId, memberandRoleRequestList: selected }
      );
      setToastMsg("Members added successfully!");
      setToastType("success");
      setToastOpen(true);
      setTimeout(() => onConfirm(selected), 600);
    } catch {
      setToastMsg("Failed to add members.");
      setToastType("error");
      setToastOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="um223_modalWrap">
      <div className="UpdateProjectMembers-wrapper">
        <h2>Update Project Members</h2>

        <input
          className="UpdateProjectMembers-search-bar"
          placeholder="Search by name, department or role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="UpdateProjectMembers-table-container">
          <table
            className="UpdateProjectMembers-table"
            aria-busy={loading}
          >
            <thead>
              <tr>
                <th style={{ width: 60 }} />
                <th>Name</th>
                <th>Department</th>
                <th>Role</th>
                <th style={{ width: 140 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {/* NOT MEMBERS */}
              {notMembers.map((emp) => {
                const checked = !!addedMap[emp.empId];
                const disabled = Number(emp.empId) === Number(creatorEmpId);

                return (
                  <tr key={`n-${emp.empId}`}>
                    <td>
                      <CheckList
                        id={`add-${emp.empId}`}
                        checked={checked}
                        onChange={(e) => handleCheckboxChange(emp, e)}
                        disabled={disabled}
                      />
                    </td>

                    <td>
                      <div className="UpdateProjectMembers-employee-info">
                        <div
                          className="UpdateProjectMembers-avatar"
                          style={{
                            backgroundColor: getColorForName(emp.empName),
                          }}
                        >
                          {getInitial(emp.empName)}
                        </div>
                        <div>
                          <div>{emp.empName}</div>
                          <div className="UpdateProjectMembers-muted">
                            ID: {emp.empId}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>{emp.departmentName ?? "—"}</td>

                    <td>
                      <div className="UpdateProjectMembers-rolePills">
                        {ROLE_OPTIONS.map((role) => {
                          const active =
                            selectedRoles[emp.empId] === role.value;

                          return (
                            <div
                              key={role.value}
                              className={`UpdateProjectMembers-rolePill ${
                                active ? "active" : ""
                              }`}
                              onClick={() =>
                                handleRolePillClick(
                                  emp.empId,
                                  role.value,
                                  disabled
                                )
                              }
                            >
                              {role.label}
                            </div>
                          );
                        })}
                      </div>
                    </td>

                    <td className="UpdateProjectMembers-muted">
                      {checked ? "Added" : "New"}
                    </td>
                  </tr>
                );
              })}

              {/* ALREADY MEMBERS */}
              {members.map((mem) => (
                <tr key={`m-${mem.empId}`}>
                  <td />
                  <td>
                    <div className="UpdateProjectMembers-employee-info">
                      <div
                        className="UpdateProjectMembers-avatar"
                        style={{
                          backgroundColor: getColorForName(mem.empName),
                        }}
                      >
                        {getInitial(mem.empName)}
                      </div>
                      <div>
                        <div>{mem.empName}</div>
                        <div className="UpdateProjectMembers-muted">
                          ID: {mem.empId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{mem.departmentName ?? "—"}</td>
                  <td>{mem.roleType ?? mem.roleName ?? "—"}</td>
                  <td className="UpdateProjectMembers-already">
                    Already Member
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="UpdateProjectMembers-footer">
          <button
            className="UpdateProjectMembers-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="UpdateProjectMembers-confirm"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Confirm"}
          </button>
        </div>

        {toastOpen && (
          <Toast
            message={toastMsg}
            type={toastType}
            onClose={() => setToastOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
